"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Biohazard, Droplets, Flame, RotateCcw, Volume2, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const sewerIssues = [{
  id: "backup",
  title: "Sewer Backup",
  icon: RotateCcw,
  risk: "High Risk of Contamination",
  description: "Water or wastewater pushing back up through basement drains, bathtubs, or floor grates requires immediate shutdown to prevent structural contamination."
}, {
  id: "smell",
  title: "Sewer Smell",
  icon: Flame,
  risk: "Air Quality Hazard",
  description: "Strong rotten egg or sewage odors inside or outside indicate trapped hydrogen sulfide gas, cracked vents, or broken underground main pipes."
}, {
  id: "slow-drain",
  title: "Slow Main Drain",
  icon: Droplets,
  risk: "Developing Blockage",
  description: "When every fixture in the home drains sluggishly at the same time, the main lateral line has heavy tree roots, grease buildup, or a deep obstruction."
}, {
  id: "coming-up",
  title: "Sewage Coming Up",
  icon: Biohazard,
  risk: "Critical Emergency",
  description: "Raw blackwater visibly rising in ground-level showers or toilets when laundry or dishwashers run requires emergency line clearance immediately."
}, {
  id: "gurgling",
  title: "Gurgling Drains",
  icon: Volume2,
  risk: "Airflow Vent Pressure",
  description: "Bubbling or gurgling sounds from toilets and sinks when other fixtures drain point to trapped air pockets caused by a choked main sewer pipe."
}, {
  id: "line-problems",
  title: "Sewer Line Problems",
  icon: Wrench,
  risk: "Pipe Collapse or Shift",
  description: "Soggy lawn spots, sinkholes, or frequent backups mean cracked, bellied, or age-corroded sewer lines needing camera inspection and repair."
}];
export default function SewerProblems() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="sewerproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider border-destructive/30 bg-destructive/10 text-destructive rounded-full">
              <AlertTriangle className="size-3.5 mr-1" />
              Emergency Triage
            </Badge>
          </div>
          <h2 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            Sewer Problems
          </h2>
          <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed">
            Main sewer line failures threaten your home with contaminated water and dangerous gas. Identify your symptoms below for immediate emergency service in Jonesboro.
          </p>
        </div>

        {/* Triage Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {sewerIssues.map((issue, index) => {
          const IconComponent = issue.icon;
          return <motion.div key={issue.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.35,
            delay: index * 0.06
          }} className="h-full">
                <Card className="h-full bg-card text-card-foreground border border-border hover:border-destructive/40 transition-colors shadow-sm hover:shadow-md flex flex-col justify-between p-6 rounded-xl">
                  <div>
                    <CardHeader className="p-0 mb-4">
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <div className="w-12 h-12 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
                          <IconComponent className="size-6" />
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground">
                          {issue.risk}
                        </span>
                      </div>
                      <CardTitle className="font-default text-xl font-bold text-card-foreground">
                        {issue.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className="font-default text-sm text-muted-foreground leading-relaxed">
                        {issue.description}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom CTA */}
        <div className="flex justify-center items-center">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 py-6 rounded-md shadow-sm">
            <Link to="/sewer-line-repair/" className="inline-flex items-center gap-2">
              <span>View Sewer Problems</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}